import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as thunks from './thunks';
import * as productThunks from '../product/thunks';
import { CommonAuditParams, InventoryAuditState } from './types';
import {
  GetBranchCategoryData,
  GetProductWithTotalAvailableItemsData
} from 'src/types';
import { cloneDeep } from 'lodash';

const initialState: InventoryAuditState = {
  inventoryAuditProductListing: [],
  isLoading: false,
  commonParams: {},
  selectedAuditProductList: []
};

const updateInventoryAuditProductListing = (
  state: InventoryAuditState,
  { payload }: PayloadAction<GetProductWithTotalAvailableItemsData[]>
) => {
  state.inventoryAuditProductListing = payload;
};

const commonAuditParamsAction = (
  state: InventoryAuditState,
  { payload }: PayloadAction<CommonAuditParams>
) => {
  state.commonParams = payload;
};

const productToBeAuditAction = (
  state: InventoryAuditState,
  { payload }: PayloadAction<GetBranchCategoryData>
) => {
  state.selectedAuditProductList = [...state.selectedAuditProductList, payload];
};

const newProductToBeAuditAction = (
  state: InventoryAuditState,
  { payload }: PayloadAction<GetBranchCategoryData[]>
) => {
  state.selectedAuditProductList = payload;
};

const deleteSerials = (
  state: InventoryAuditState,
  { payload }: PayloadAction<{ id: number; serials: string[] }>
) => {
  const inventoryAuditProductListingClone = cloneDeep(
    state.inventoryAuditProductListing
  );

  // coded for ts error security
  if (!inventoryAuditProductListingClone?.[0]) {
    return;
  }

  const newAuditProduct = inventoryAuditProductListingClone.find(
    (i) => i.id === payload.id
  );

  const newSerials =
    newAuditProduct?.scanned_serial_nos?.filter((oldSn) => {
      const isForDeleted = payload.serials.includes(oldSn);
      return !isForDeleted;
    }) ?? [];

  inventoryAuditProductListingClone.splice(
    inventoryAuditProductListingClone.findIndex(
      (i) => i.id === newAuditProduct?.id
    ),
    1,
    {
      ...newAuditProduct,
      scanned_serial_nos: newSerials,
      audited_count: newSerials?.length
    }
  );

  state.inventoryAuditProductListing = inventoryAuditProductListingClone;
};

const resetAuditStateAction = (state: InventoryAuditState) => {
  state.inventoryAuditProductListing = [];
  state.selectedAuditProductList = [];
  state.commonParams = {};
};

const resetInventoryAuditListing = (state: InventoryAuditState) => {
  state.inventoryAuditProductListing = [];
  state.selectedAuditProductList = [];
};

const slice = createSlice({
  name: 'inventory-audit',
  initialState,
  reducers: {
    updateInventoryAuditProductListing,
    commonAuditParamsAction,
    productToBeAuditAction,
    newProductToBeAuditAction,
    deleteSerials,
    resetAuditStateAction,
    resetInventoryAuditListing
  },
  extraReducers(builder) {
    builder.addCase(
      productThunks.getProductWithTotalAvailableItemThunk.pending,
      (state) => {
        state.isLoading = true;
      }
    );
    builder.addCase(
      productThunks.getProductWithTotalAvailableItemThunk.rejected,
      (state) => {
        state.isLoading = false;
      }
    );
    builder.addCase(
      productThunks.getProductWithTotalAvailableItemThunk.fulfilled,
      (state, { payload }) => {
        state.isLoading = false;

        // Preserve the previous state and add new items from the payload
        const existingItems = state.inventoryAuditProductListing || [];
        const newItems = (payload?.originalData?.data || []).map((item) => ({
          ...item,
          audited_count: 0
        }));

        state.inventoryAuditProductListing = [...existingItems, ...newItems];
      }
    );
  }
});

export const reducer = slice.reducer;

export const actions = {
  ...slice.actions,
  ...thunks
};
